import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import api from '../../services/api';
import { toast } from 'sonner';
import { 
  Building2, 
  Users, 
  GraduationCap, 
  IndianRupee, 
  Plus, 
  TrendingUp,
  School,
  Loader2,
  Copy,
  CheckCircle,
  XCircle,
  Download,
  Upload
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [schools, setSchools] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [createdSchool, setCreatedSchool] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    phone: '',
    email: '',
    annual_fee: 25000,
    plan: 'basic',
    max_students: 500
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashboardRes, schoolsRes, revenueRes] = await Promise.all([
        api.get('/superadmin/dashboard'),
        api.get('/superadmin/schools'),
        api.get('/superadmin/revenue')
      ]);
      setStats(dashboardRes.data);
      setSchools(schoolsRes.data.schools || []);
      setRevenue(revenueRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchool = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      toast.error('Name and code are required');
      return;
    }

    setFormLoading(true);
    try {
      const response = await api.post('/superadmin/schools', {
        ...formData,
        is_active: true
      });
      setCreatedSchool(response.data);
      toast.success('School created successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create school');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (schoolId, currentStatus) => {
    try {
      await api.put(`/superadmin/schools/${schoolId}`, {
        ...schools.find(s => s.id === schoolId),
        is_active: !currentStatus
      });
      toast.success(`School ${currentStatus ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update school status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleExportSchool = async (schoolCode) => {
    try {
      toast.info(`Exporting ${schoolCode}...`);
      const res = await api.get(`/superadmin/export-school/${schoolCode}`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `school-${schoolCode}-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${schoolCode} exported`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
  };

  const handleImportSchool = async (file, overwrite) => {
    if (!file) return;
    try {
      toast.info('Importing school dump...');
      const fd = new FormData();
      fd.append('file', file);
      // Explicitly set multipart/form-data so axios generates a proper
      // boundary. The global default is 'application/json' which, on some
      // production builds, prevents axios from auto-detecting FormData and
      // causes the browser to send the body without a boundary → server sees
      // 'body.file: field required' (HTTP 422).
      const res = await api.post(
        `/superadmin/import-school?overwrite=${overwrite ? 'true' : 'false'}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const report = res.data;
      const totalInserted = Object.values(report.inserted || {}).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Imported ${report.school}: ${totalInserted} docs inserted`);
      loadData();
    } catch (e) {
      // Safely convert any error `detail` into a human-readable string.
      // FastAPI's 422 responses return `detail` as an ARRAY of ValidationError
      // objects — passing that directly to <Toaster/> crashes React
      // (Error #31: "Objects are not valid as a React child") and leaves a
      // blank screen. Always coerce to string.
      const raw = e.response?.data?.detail;
      let msg = 'Import failed';
      if (typeof raw === 'string') {
        msg = raw;
      } else if (Array.isArray(raw) && raw.length) {
        msg = raw.map(r => {
          if (typeof r === 'string') return r;
          const loc = Array.isArray(r?.loc) ? r.loc.join('.') : '';
          return `${loc}: ${r?.msg || JSON.stringify(r)}`;
        }).join(' · ');
      } else if (raw && typeof raw === 'object') {
        msg = JSON.stringify(raw);
      } else if (e.message) {
        msg = e.message;
      }
      console.error('Import school error:', e.response?.status, e.response?.data || e);
      toast.error(`Import failed (${e.response?.status || '—'}): ${msg}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      address: '',
      phone: '',
      email: '',
      annual_fee: 25000,
      plan: 'basic',
      max_students: 500
    });
    setCreatedSchool(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8 text-primary" />
      </div>
    );
  }

  const planColors = {
    basic: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    pro: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
    enterprise: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20'
  };

  return (
    <div className="space-y-6" data-testid="superadmin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Super Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">Manage all schools and view platform metrics</p>
        </div>
        <Dialog open={isCreateModalOpen} onOpenChange={(open) => { setIsCreateModalOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-school-button">
              <Plus className="h-4 w-4 mr-2" />
              Add School
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{createdSchool ? 'School Created!' : 'Add New School'}</DialogTitle>
            </DialogHeader>
            
            {createdSchool ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-center font-medium">{createdSchool.name} has been created!</p>
                </div>
                
                <div className="space-y-3">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">School Code</p>
                    <div className="flex items-center justify-between">
                      <p className="font-mono font-bold text-lg">{createdSchool.code}</p>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdSchool.code)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Admin Email</p>
                    <div className="flex items-center justify-between">
                      <p className="font-mono">{createdSchool.admin_email}</p>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdSchool.admin_email)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Admin Password</p>
                    <div className="flex items-center justify-between">
                      <p className="font-mono">{createdSchool.admin_password}</p>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdSchool.admin_password)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                <Button onClick={() => { setIsCreateModalOpen(false); resetForm(); }} className="w-full">
                  Done
                </Button>
              </div>
            ) : (
              <form onSubmit={handleCreateSchool} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>School Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="ABC School"
                      required
                      data-testid="school-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>School Code *</Label>
                    <Input
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="ABC123"
                      maxLength={10}
                      className="font-mono"
                      required
                      data-testid="school-code-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="School address"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Phone number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="school@email.com"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Annual Fee (₹)</Label>
                    <Input
                      type="number"
                      value={formData.annual_fee}
                      onChange={(e) => setFormData({ ...formData, annual_fee: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Students</Label>
                    <Input
                      type="number"
                      value={formData.max_students}
                      onChange={(e) => setFormData({ ...formData, max_students: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Plan</Label>
                    <Select value={formData.plan} onValueChange={(val) => setFormData({ ...formData, plan: val })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basic">Basic</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={formLoading}>
                    {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Create School
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Schools</p>
                <p className="text-3xl font-heading font-bold">{stats?.total_schools || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-primary/10">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Students</p>
                <p className="text-3xl font-heading font-bold">{stats?.total_students || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-secondary/10">
                <GraduationCap className="h-6 w-6 text-secondary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-3xl font-heading font-bold text-green-500">₹{(stats?.total_revenue || 0).toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/10">
                <IndianRupee className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">This Month</p>
                <p className="text-3xl font-heading font-bold text-blue-500">₹{(stats?.monthly_revenue || 0).toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/10">
                <TrendingUp className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart */}
      {revenue && (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Monthly Revenue - {revenue.year}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 min-h-[256px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenue.monthly_data}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="month_name" className="text-muted-foreground" tick={{ fontSize: 12 }} />
                  <YAxis className="text-muted-foreground" tickFormatter={(val) => `₹${(val/1000).toFixed(0)}k`} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']}
                  />
                  <Bar dataKey="revenue" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schools Table */}
      <Card className="border border-border">
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <School className="h-5 w-5 text-primary" />
              All Schools ({schools.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              <input
                id="import-school-file"
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const overwrite = window.confirm(
                    'OVERWRITE mode? Click OK to delete any existing data for this school first (use when re-importing). Click Cancel for safe merge (skip existing).'
                  );
                  handleImportSchool(f, overwrite);
                  e.target.value = '';
                }}
                data-testid="import-school-file-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('import-school-file').click()}
                data-testid="import-school-button"
              >
                <Upload className="h-4 w-4 mr-2" />
                Import School JSON
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {schools.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>School</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Students</TableHead>
                    <TableHead>Teachers</TableHead>
                    <TableHead>Collected</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Export</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schools.map((school) => (
                    <TableRow key={school.id} className="table-row-hover">
                      <TableCell>
                        <div>
                          <p className="font-medium">{school.name}</p>
                          <p className="text-xs text-muted-foreground">{school.email || '-'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="px-2 py-1 bg-muted rounded text-sm">{school.code}</code>
                      </TableCell>
                      <TableCell>{school.student_count || 0}</TableCell>
                      <TableCell>{school.teacher_count || 0}</TableCell>
                      <TableCell className="text-green-600">₹{(school.total_collected || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge className={planColors[school.plan] || planColors.basic}>
                          {school.plan || 'basic'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {school.is_active ? (
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Active</Badge>
                        ) : (
                          <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch 
                          checked={school.is_active}
                          onCheckedChange={() => handleToggleActive(school.id, school.is_active)}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleExportSchool(school.code)}
                          data-testid={`export-school-${school.code}`}
                          title={`Download ${school.code} as JSON`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No schools yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cost Breakdown Card */}
      <Card className="border border-border bg-gradient-to-br from-primary/5 to-secondary/5">
        <CardHeader>
          <CardTitle>SaaS Maintenance Cost Breakdown (Estimated)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <h4 className="font-medium">Infrastructure (per month)</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cloud Server (AWS/DigitalOcean)</span>
                  <span>₹2,000 - ₹5,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">MongoDB Atlas</span>
                  <span>₹0 - ₹3,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Domain & SSL</span>
                  <span>₹100/month</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium">Notifications (per school/month)</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">SMS (Twilio) ~500 msgs</span>
                  <span>₹500 - ₹1,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email (SendGrid) ~1000 emails</span>
                  <span>₹0 - ₹500</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium">Revenue Potential</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Per School (420 students)</span>
                  <span className="text-green-500">₹3,000 - ₹5,000/month</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">10 Schools</span>
                  <span className="text-green-500">₹30,000 - ₹50,000/month</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span>Profit Margin</span>
                  <span className="text-green-500">60-70%</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminDashboard;
